import React, { useEffect, useMemo, useState } from "react";
import Footer from "./Footer";
import Header from "./Header";
import axios from "axios";
import Swal from "sweetalert2";
import config from "../../config";

const GROUPS = [
  {
    key: "main",
    label: "Main Navigation",
  },
  {
    key: "tools",
    label: "Tools",
  },
  {
    key: "billing",
    label: "Billing",
  },
  {
    key: "reports",
    label: "Reports",
  },
  {
    key: "organisation",
    label: "Organisation",
  },
  {
    key: "support",
    label: "Support",
  },
  {
    key: "integrations",
    label: "Integrations",
  },
  {
    key: "settings",
    label: "Settings",
  },
];

const MODULES = [
  {
    key: "order",
    group: "main",
    label: "Orders",
    ic: "bi bi-bag-check",
    sub: "Create, edit and cancel orders",
  },
  {
    key: "shipment",
    group: "main",
    label: "Shipments",
    ic: "bi bi-truck",
    sub: "Manifests, AWBs and tracking",
  },
  {
    key: "pickup",
    group: "main",
    label: "Pickup Requests",
    ic: "bi bi-arrow-up-square",
    sub: "Raise and manage pickups",
  },
  {
    key: "ndr",
    group: "main",
    label: "NDR",
    ic: "bi bi-exclamation-octagon",
    sub: "Failed delivery reports",
  },
  {
    key: "weightdisc",
    group: "main",
    label: "Weight Discrepancy",
    ic: "bi bi-box2",
    sub: "Weight mismatch review",
  },
  {
    key: "ratecard",
    group: "main",
    label: "Rate Card",
    ic: "bi bi-receipt",
    sub: "Courier rate cards",
  },
  {
    key: "warehouse",
    group: "main",
    label: "Warehouse",
    ic: "bi bi-box-seam",
    sub: "Inventory and stock movement",
  },

  {
    key: "ratecalc",
    group: "tools",
    label: "Rate Calculator",
    ic: "bi bi-calculator",
    sub: "Rate and profitability check",
  },
  {
    key: "allocation",
    group: "tools",
    label: "Order Allocation",
    ic: "bi bi-diagram-3",
    sub: "Allocation engine",
  },
  {
    key: "shippingrules",
    group: "tools",
    label: "Shipping Rules",
    ic: "bi bi-journal-bookmark",
    sub: "Automated courier rules",
  },
  {
    key: "notifsettings",
    group: "tools",
    label: "Notification Settings",
    ic: "bi bi-bell",
    sub: "Notification configuration",
  },
  {
    key: "aftership",
    group: "tools",
    label: "AfterShip",
    ic: "bi bi-truck-front",
    sub: "Shipment tracking",
  },
  {
    key: "boxes",
    group: "tools",
    label: "Boxes",
    ic: "bi bi-box",
    sub: "Box master",
  },

  {
    key: "shippingcharges",
    group: "billing",
    label: "Shipping Charges",
    ic: "bi bi-truck",
    sub: "Freight billing",
  },
  {
    key: "codremittance",
    group: "billing",
    label: "COD Remittance",
    ic: "bi bi-cash-coin",
    sub: "COD payouts",
  },
  {
    key: "passbook",
    group: "billing",
    label: "Passbook",
    ic: "bi bi-journal-text",
    sub: "Ledger entries",
  },
  {
    key: "deduction",
    group: "billing",
    label: "Deduction",
    ic: "bi bi-dash-circle",
    sub: "Deductions",
  },
  {
    key: "recharge",
    group: "billing",
    label: "Recharge",
    ic: "bi bi-plus-circle",
    sub: "Recharges",
  },
  {
    key: "refund",
    group: "billing",
    label: "Refund",
    ic: "bi bi-arrow-counterclockwise",
    sub: "Refunds",
  },
  {
    key: "weightrecon",
    group: "billing",
    label: "Weight Reconciliation",
    ic: "bi bi-arrow-left-right",
    sub: "Weight vs billed",
  },

  {
    key: "shpreports",
    group: "reports",
    label: "Shipment Reports",
    ic: "bi bi-bar-chart-line",
    sub: "Report exports",
  },
  {
    key: "invoices",
    group: "reports",
    label: "Invoices",
    ic: "bi bi-file-earmark-text",
    sub: "Invoice records",
  },

  {
    key: "team",
    group: "organisation",
    label: "Team",
    ic: "bi bi-people",
    sub: "Members and access",
  },

  {
    key: "tickets",
    group: "support",
    label: "Support Tickets",
    ic: "bi bi-headset",
    sub: "Customer tickets",
  },
  {
    key: "faq",
    group: "support",
    label: "Support FAQs",
    ic: "bi bi-question-circle",
    sub: "Knowledge base",
  },

  {
    key: "channels",
    group: "integrations",
    label: "Channel Integration",
    ic: "bi bi-broadcast",
    sub: "Storefront channels",
  },
  {
    key: "apis",
    group: "integrations",
    label: "Developer APIs",
    ic: "bi bi-code-slash",
    sub: "API credentials",
  },

  {
    key: "labelsettings",
    group: "settings",
    label: "Label Settings",
    ic: "bi bi-tag",
    sub: "Label templates and print",
  },
];

const getUserData = () => {
  try {
    const data = localStorage.getItem("ship_rocket_user");

    return data ? JSON.parse(data) : {};
  } catch (error) {
    return {};
  }
};

const createBlankPrivileges = () => {
  const result = {};

  MODULES.forEach((module) => {
    result[module.key] = false;
  });

  return result;
};

const normalizePrivileges = (privileges = {}) => {
  const result = {};

  MODULES.forEach((module) => {
    if (typeof privileges[module.key] === "boolean") {
      result[module.key] = privileges[module.key];
    } else if (
      privileges[module.key] &&
      typeof privileges[module.key] === "object"
    ) {
      /*
       * Temporary compatibility with
       * old Add/View/Delete data.
       *
       * Once backend is migrated,
       * this can be removed.
       */
      result[module.key] = privileges[module.key].view === true;
    } else {
      result[module.key] = false;
    }
  });

  return result;
};

const Create_team_user = () => {
  const [loading, setLoading] = useState(false);

  const [memberLoading, setMemberLoading] = useState(false);

  const [editingMember, setEditingMember] = useState(null);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [role, setRole] = useState("Member");
  const [warehouse, setWarehouse] = useState("All India");
  const [status, setStatus] = useState("active");

  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");

  const [showPassword, setShowPassword] = useState(false);

  const [privileges, setPrivileges] = useState(createBlankPrivileges());

  const [errors, setErrors] = useState({});

  const userData = getUserData();

  const isTeamMember = Number(userData?.user_type) === 2;

  const canManageTeam =
    !isTeamMember ||
    userData?.previlage?.team === true ||
    userData?.previlage?.team?.add === true;

  const queryParams = useMemo(
    () => new URLSearchParams(window.location.search),
    []
  );

  const editId = queryParams.get("id");

  const duplicateId = queryParams.get("duplicate");

  const isEditing = editId && editId !== "new";

  const selectedModuleCount = MODULES.filter(
    (module) => privileges[module.key] === true
  ).length;

  const getTeamMembers = async () => {
    try {
      const response = await axios.post(
        `${config.apiUrl}/user/get_team_members`,
        {},
        {
          headers: config.headers,
        }
      );

      if (response.data?.status === true) {
        return Array.isArray(response.data.output) ? response.data.output : [];
      }

      return [];
    } catch (error) {
      console.log("get_team_members error", error);

      return [];
    }
  };

  const loadMember = async (memberId) => {
    try {
      setMemberLoading(true);

      const list = await getTeamMembers();

      const member = list.find(
        (item) => String(item.staff_id || item.id) === String(memberId)
      );

      if (!member) {
        Swal.fire({
          icon: "error",
          text: "Team member not found.",
        });

        window.location.href = "/team";

        return;
      }

      setEditingMember(member);

      setName(member.full_name || member.name || "");

      setEmail(member.email || "");

      setMobile(member.mobile || "");

      setRole(member.role || "Member");

      setWarehouse(member.warehouse || "All India");

      setStatus(Number(member.dstatus) === 1 ? "active" : "disabled");

      setPrivileges(
        normalizePrivileges(member.previlage || member.perms || {})
      );
    } catch (error) {
      console.log("loadMember error", error);

      Swal.fire({
        icon: "error",
        text: error?.response?.data?.message || "Unable to load team member.",
      });
    } finally {
      setMemberLoading(false);
    }
  };

  const loadDuplicateMember = async () => {
    if (!duplicateId) {
      return;
    }

    try {
      setMemberLoading(true);

      const list = await getTeamMembers();

      const member = list.find(
        (item) => String(item.staff_id || item.id) === String(duplicateId)
      );

      if (!member) {
        return;
      }

      setName(`Copy of ${member.full_name || member.name || ""}`);

      /*
       * Email must be entered again because
       * it must remain unique.
       */
      setEmail("");

      setMobile(member.mobile || "");

      setRole(member.role || "Member");

      setWarehouse(member.warehouse || "All India");

      /*
       * Duplicate remains disabled,
       * matching the original behavior.
       */
      setStatus("disabled");

      setPrivileges(
        normalizePrivileges(member.previlage || member.perms || {})
      );
    } catch (error) {
      console.log("duplicate member error", error);
    } finally {
      setMemberLoading(false);
    }
  };

  useEffect(() => {
    if (!canManageTeam) {
      return;
    }

    if (isEditing) {
      loadMember(editId);
    } else if (duplicateId) {
      loadDuplicateMember();
    }
  }, [editId, duplicateId, canManageTeam]);

  const togglePrivilege = (moduleKey) => {
    setPrivileges((previous) => ({
      ...previous,

      [moduleKey]: previous[moduleKey] !== true,
    }));
  };

  const setAllPrivileges = (value) => {
    const result = {};

    MODULES.forEach((module) => {
      result[module.key] = value;
    });

    setPrivileges(result);
  };

  const validate = () => {
    const nextErrors = {};

    if (!name.trim()) {
      nextErrors.name = true;
    }

    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      nextErrors.email = true;
    }

    if (mobile.trim() && !/^[6-9]\d{9}$/.test(mobile.trim())) {
      nextErrors.mobile = true;
    }

    /*
     * Password is mandatory when creating.
     *
     * On edit, blank means keep old password.
     */
    if (!isEditing) {
      if (!password) {
        nextErrors.password = true;
      } else if (password.length < 8) {
        nextErrors.password = true;
      }

      if (password !== password2) {
        nextErrors.password2 = true;
      }
    } else {
      if (password && password.length < 8) {
        nextErrors.password = true;
      }

      if (password && password !== password2) {
        nextErrors.password2 = true;
      }
    }

    setErrors(nextErrors);

    return Object.keys(nextErrors).length === 0;
  };

  const saveMember = async () => {
    if (!canManageTeam) {
      Swal.fire({
        icon: "warning",
        text: "You do not have permission to manage team members.",
      });

      return;
    }

    if (!validate()) {
      Swal.fire({
        icon: "warning",
        text: "Please fix the highlighted fields.",
      });

      return;
    }

    try {
      setLoading(true);

      const payload = {
        full_name: name.trim(),

        email: email.trim(),

        mobile: mobile.trim(),

        role,

        warehouse,

        dstatus: status === "active" ? 1 : 0,

        /*
         * NEW privilege structure.
         */
        previlage: normalizePrivileges(privileges),
      };

      if (password) {
        payload.password = password;
      }

      if (isEditing) {
        payload.staff_id = editingMember?.staff_id || editId;
      }

      const apiUrl = isEditing
        ? `${config.apiUrl}/user/update_team_member`
        : `${config.apiUrl}/user/create_team_member`;

      const response = await axios.post(apiUrl, payload, {
        headers: config.headers,
      });

      console.log("save team member response =>", response.data);

      if (response.data?.status === true) {
        await Swal.fire({
          icon: "success",
          title: isEditing ? "Member updated." : "Member added.",
          showConfirmButton: false,
          timer: 1600,
        });

        window.location.href = "/team";
      } else {
        Swal.fire({
          icon: "error",
          text: response.data?.message || "Unable to save team member.",
        });
      }
    } catch (error) {
      console.log("save team member error", error);

      Swal.fire({
        icon: "error",
        text: error?.response?.data?.message || "Unable to save team member.",
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    if (isEditing) {
      loadMember(editId);

      setPassword("");
      setPassword2("");
      setErrors({});

      return;
    }

    setName("");
    setEmail("");
    setMobile("");
    setRole("Member");
    setWarehouse("All India");
    setStatus("active");
    setPassword("");
    setPassword2("");
    setPrivileges(createBlankPrivileges());
    setErrors({});
  };

  const passwordScore = (() => {
    let score = 0;

    if (password.length >= 8) {
      score++;
    }

    if (/[A-Z]/.test(password)) {
      score++;
    }

    if (/[0-9]/.test(password)) {
      score++;
    }

    if (/[^A-Za-z0-9]/.test(password)) {
      score++;
    }

    return score;
  })();

  const passwordMessage =
    passwordScore === 4
      ? "Strong password."
      : passwordScore === 3
      ? "Good — add one more condition to strengthen it."
      : password
      ? "Use 8+ characters with a number and a symbol."
      : "Use 8+ characters with a number and a symbol.";

  if (!canManageTeam) {
    return (
      <div>
        <Header>
          <div className="content">
            <div className="ol-header">
              <div className="ol-hd">
                <a
                  href="/team"
                  className="ol-back"
                  title="Back to team members"
                >
                  <i className="bi bi-arrow-left"></i>
                </a>

                <div>
                  <div className="ol-title">Team Members</div>

                  <div className="ol-sub">
                    <span className="dot"></span> Organisation
                  </div>
                </div>
              </div>
            </div>

            <div className="tm-wrap">
              <div className="card-base tm-table-card">
                <div
                  style={{
                    padding: "50px",
                    textAlign: "center",
                  }}
                >
                  <div className="ol-empty-ico">
                    <i className="bi bi-shield-lock"></i>
                  </div>

                  <h4>Access Restricted</h4>

                  <p>You do not have permission to manage team members.</p>
                </div>
              </div>
            </div>
          </div>
        </Header>

        <Footer />
      </div>
    );
  }

  return (
    <div>
      <Header>
        <div className="content">
          <div className="ol-header">
            <div className="ol-hd">
              <a href="/team" className="ol-back" title="Back to team members">
                <i className="bi bi-arrow-left"></i>
              </a>

              <div>
                <div className="ol-title" id="tmFormTitle">
                  {isEditing ? "Edit Team Member" : "Add Team Member"}
                </div>

                <div className="ol-sub">
                  <span className="dot"></span> Organisation{" "}
                  <span
                    className="dot"
                    style={{
                      background: "var(--t4)",
                    }}
                  ></span>{" "}
                  <a href="/team" className="ol-link">
                    Team Members
                  </a>
                </div>
              </div>
            </div>

            <div className="ol-actions">
              <a href="/team" className="ol-btn ghost">
                Cancel
              </a>

              <button
                type="button"
                className="ol-btn primary"
                id="tmSaveBtn"
                onClick={saveMember}
                disabled={loading || memberLoading}
              >
                <i className="bi bi-check2"></i>{" "}
                {loading ? "Saving..." : "Save Member"}
              </button>
            </div>
          </div>

          <div className="tm-wrap tm-form-wrap" id="tmFormRoot">
            <div className="tm-layout">
              <aside className="tm-rail">
                <div className="card-base tm-rail-card">
                  <div className="tm-rail-title">Sections</div>

                  <nav className="tm-rail-nav" id="tmRailNav">
                    <a href="#tmSec1" className="tm-rail-item active">
                      <span className="dot"></span> Member Information
                    </a>

                    <a href="#tmSec2" className="tm-rail-item">
                      <span className="dot"></span> Login &amp; Password
                    </a>

                    <a href="#tmSec3" className="tm-rail-item">
                      <span className="dot"></span> Manage Privileges
                    </a>

                    <div className="tm-rail-group" id="tmRailPerms">
                      {GROUPS.map((group) => {
                        const modules = MODULES.filter(
                          (module) => module.group === group.key
                        );

                        return (
                          <div className="tm-rail-grp" key={group.key}>
                            <span className="tm-rail-grp-lbl">
                              {group.label}
                            </span>

                            {modules.map((module) => (
                              <a
                                href={`#tmMod-${module.key}`}
                                className="tm-rail-sub"
                                key={module.key}
                              >
                                <i
                                  className={`${module.ic} tm-rail-sub-ic`}
                                ></i>

                                <span>{module.label}</span>
                              </a>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  </nav>
                </div>

                <div className="card-base tm-summary-card">
                  <div className="tm-summary-title">Member Summary</div>

                  <div className="tm-summary-row">
                    <span>Name</span>

                    <b id="tmSumName">{name || "—"}</b>
                  </div>

                  <div className="tm-summary-row">
                    <span>Role</span>

                    <b id="tmSumRole">{role || "—"}</b>
                  </div>

                  <div className="tm-summary-row">
                    <span>Modules</span>

                    <b id="tmSumMods">
                      {selectedModuleCount}/{MODULES.length}
                    </b>
                  </div>

                  <div className="tm-summary-row">
                    <span>Access</span>

                    <b id="tmSumPriv">{selectedModuleCount}</b>
                  </div>

                  <div className="tm-summary-divider"></div>

                  <div className="tm-summary-row">
                    <span>Status</span>

                    <b id="tmSumStatus">
                      {status === "active" ? "Active" : "Disabled"}
                    </b>
                  </div>

                  <small className="tm-summary-hint">
                    Only selected modules will be available in this member's
                    sidebar.
                  </small>
                </div>
              </aside>

              <main className="tm-main">
                <section className="card-base tm-section" id="tmSec1">
                  <div className="oa-block-head">
                    <div>
                      <h2 className="oa-block-title">
                        <i className="bi bi-person"></i> Member Information
                      </h2>

                      <p className="oa-block-sub">
                        Basic details used to identify this member across the
                        panel.
                      </p>
                    </div>
                  </div>

                  <div className="tm-grid">
                    <div className="ol-field tm-col-6">
                      <label>
                        Full Name <span className="req">*</span>
                      </label>

                      <input
                        type="text"
                        id="tmName"
                        placeholder="e.g. Rahul Sharma"
                        autoComplete="off"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                      />

                      <small
                        className="tm-field-error"
                        id="tmNameErr"
                        hidden={!errors.name}
                      >
                        Please enter the member's full name
                      </small>
                    </div>

                    <div className="ol-field tm-col-6">
                      <label>
                        Email <span className="req">*</span>
                      </label>

                      <input
                        type="email"
                        id="tmEmail"
                        placeholder="e.g. rahul.sharma@skyship.com"
                        autoComplete="off"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />

                      <small
                        className="tm-field-error"
                        id="tmEmailErr"
                        hidden={!errors.email}
                      >
                        Please enter a valid email address
                      </small>
                    </div>

                    <div className="ol-field tm-col-6">
                      <label>Mobile</label>

                      <input
                        type="text"
                        id="tmMobile"
                        placeholder="e.g. 9876543210"
                        inputMode="numeric"
                        maxLength="10"
                        autoComplete="off"
                        value={mobile}
                        onChange={(e) =>
                          setMobile(e.target.value.replace(/\D/g, ""))
                        }
                      />

                      <small
                        className="tm-field-error"
                        id="tmMobileErr"
                        hidden={!errors.mobile}
                      >
                        Enter a valid 10-digit mobile number
                      </small>
                    </div>

                    <div className="ol-field tm-col-6">
                      <label>Role</label>

                      <select
                        id="tmRole"
                        value={role}
                        onChange={(e) => setRole(e.target.value)}
                      >
                        <option value="Member">Member</option>
                      </select>
                    </div>

                    <div className="ol-field tm-col-6">
                      <label>Warehouse / Region</label>

                      <select
                        id="tmWarehouse"
                        value={warehouse}
                        onChange={(e) => setWarehouse(e.target.value)}
                      >
                        <option value="All India">All India</option>

                        <option value="Mumbai Hub">Mumbai Hub</option>

                        <option value="Delhi Hub">Delhi Hub</option>

                        <option value="Bengaluru Hub">Bengaluru Hub</option>

                        <option value="Kolkata Hub">Kolkata Hub</option>
                      </select>
                    </div>

                    <div className="ol-field tm-col-6">
                      <label>Account Status</label>

                      <select
                        id="tmStatus"
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                      >
                        <option value="active">Active</option>

                        <option value="disabled">Disabled</option>
                      </select>
                    </div>
                  </div>
                </section>

                <section className="card-base tm-section" id="tmSec2">
                  <div className="oa-block-head">
                    <div>
                      <h2 className="oa-block-title">
                        <i className="bi bi-key"></i> Login &amp; Password
                      </h2>

                      <p className="oa-block-sub">
                        Set the password this member will use to sign in to the
                        panel.
                      </p>
                    </div>
                  </div>

                  <div className="tm-grid">
                    <div className="ol-field tm-col-6">
                      <label>Password</label>

                      <div className="tm-pw">
                        <input
                          type={showPassword ? "text" : "password"}
                          id="tmPassword"
                          placeholder="Enter password"
                          autoComplete="new-password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                        />

                        <button
                          type="button"
                          className="tm-pw-btn"
                          id="tmPwToggle"
                          title={
                            showPassword ? "Hide password" : "Show password"
                          }
                          aria-label={
                            showPassword ? "Hide password" : "Show password"
                          }
                          onClick={() => setShowPassword((value) => !value)}
                        >
                          <i
                            className={`bi ${
                              showPassword ? "bi-eye-slash" : "bi-eye"
                            }`}
                          ></i>
                        </button>
                      </div>

                      <div
                        className={`tm-meter${
                          passwordScore ? ` s-${passwordScore}` : ""
                        }`}
                      >
                        <span className="tm-meter-bar" id="tmPwBar"></span>
                      </div>

                      <small className="tm-meter-text" id="tmPwText">
                        {passwordMessage}
                      </small>

                      {errors.password && (
                        <small className="tm-field-error">
                          Password must contain at least 8 characters.
                        </small>
                      )}
                    </div>

                    <div className="ol-field tm-col-6">
                      <label>Confirm Password</label>

                      <input
                        type="password"
                        id="tmPassword2"
                        placeholder="Re-enter password"
                        autoComplete="new-password"
                        value={password2}
                        onChange={(e) => setPassword2(e.target.value)}
                      />

                      <small
                        className="tm-field-error"
                        id="tmPassword2Err"
                        hidden={!errors.password2}
                      >
                        Passwords do not match
                      </small>
                    </div>
                  </div>
                </section>

                <section className="card-base tm-section" id="tmSec3">
                  <div className="oa-block-head">
                    <div>
                      <h2 className="oa-block-title">
                        <i className="bi bi-shield-lock"></i> Manage Privileges
                      </h2>

                      <p className="oa-block-sub">
                        Select the modules this member can access from the
                        sidebar.
                      </p>
                    </div>

                    <span className="tm-badge" id="tmPrivBadge">
                      {selectedModuleCount}{" "}
                      {selectedModuleCount === 1 ? "module" : "modules"}
                    </span>
                  </div>

                  <div
                    className="tm-perm-head"
                    style={{
                      gridTemplateColumns: "1fr 80px",
                    }}
                  >
                    <span>Module</span>

                    <span className="tm-perm-act-h">Access</span>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: "8px",
                      marginBottom: "12px",
                    }}
                  >
                    <button
                      type="button"
                      className="ol-btn ghost"
                      onClick={() => setAllPrivileges(true)}
                    >
                      Select All
                    </button>

                    <button
                      type="button"
                      className="ol-btn ghost"
                      onClick={() => setAllPrivileges(false)}
                    >
                      Clear All
                    </button>
                  </div>

                  <div className="tm-perm-list" id="tmPermList">
                    {GROUPS.map((group) => {
                      const groupModules = MODULES.filter(
                        (module) => module.group === group.key
                      );

                      const selected = groupModules.filter(
                        (module) => privileges[module.key] === true
                      ).length;

                      return (
                        <React.Fragment key={group.key}>
                          <div className="tm-perm-group">
                            <span className="tm-perm-group-lbl">
                              {group.label}
                            </span>

                            <span className="tm-perm-group-cnt">
                              {selected}/{groupModules.length}
                            </span>
                          </div>

                          {groupModules.map((module) => {
                            const enabled = privileges[module.key] === true;

                            return (
                              <div
                                className="tm-perm-row"
                                id={`tmMod-${module.key}`}
                                data-key={module.key}
                                key={module.key}
                                style={{
                                  gridTemplateColumns: "1fr 80px",
                                }}
                              >
                                <div className="tm-perm-name">
                                  <span className="tm-perm-ic">
                                    <i className={module.ic}></i>
                                  </span>

                                  <span>
                                    <span className="tm-perm-title">
                                      {module.label}
                                    </span>

                                    <span className="tm-perm-sub">
                                      {module.sub}
                                    </span>
                                  </span>
                                </div>

                                <div
                                  className="tm-perm-cell"
                                  data-label="Access"
                                  style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                  }}
                                >
                                  <label
                                    className="tm-module-radio"
                                    title={
                                      enabled
                                        ? "Module enabled"
                                        : "Module disabled"
                                    }
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      cursor: "pointer",
                                    }}
                                  >
                                    <input
                                      type="radio"
                                      name={`module_access_${module.key}`}
                                      checked={enabled}
                                      onChange={() =>
                                        togglePrivilege(module.key)
                                      }
                                      style={{
                                        cursor: "pointer",
                                      }}
                                    />
                                  </label>
                                </div>
                              </div>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </section>

                <div className="tm-form-acts">
                  <button
                    type="button"
                    className="ol-btn ghost"
                    id="tmResetBtn"
                    onClick={resetForm}
                    disabled={loading}
                  >
                    <i className="bi bi-arrow-counterclockwise"></i> Reset
                  </button>

                  <a href="/team" className="ol-btn ghost">
                    Cancel
                  </a>

                  <button
                    type="button"
                    className="ol-btn primary"
                    id="tmSaveBtn2"
                    onClick={saveMember}
                    disabled={loading || memberLoading}
                  >
                    <i className="bi bi-check2"></i>{" "}
                    {loading ? "Saving..." : "Save Member"}
                  </button>
                </div>
              </main>
            </div>
          </div>
        </div>
      </Header>

      <Footer />
    </div>
  );
};

export default Create_team_user;
